import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { claimsApi, clientsApi, policiesApi } from "@/api/endpoints";
import type { CustomerProfile, Policy } from "@/api/types";
import { Modal } from "@/components/Modal";
import { US_STATES } from "@/constants/crm";
import { customerName } from "@/utils/format";

type PresetPolicy = { id: string; label: string; product_line?: string | null };

type Props = {
  open: boolean;
  onClose: () => void;
  presetPolicy?: PresetPolicy;
  onCreated?: (claimId: string) => void;
};

const inputStyle = {
  width: "100%",
  padding: "0.6rem 0.75rem",
  borderRadius: 10,
  border: "1px solid var(--border)",
  background: "var(--surface, #fff)",
};

const labelWrap = { display: "grid", gap: "0.3rem" } as const;

export function NewClaimModal({ open, onClose, presetPolicy, onCreated }: Props) {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<{ id: string; name: string } | null>(null);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<CustomerProfile[]>([]);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [policyId, setPolicyId] = useState(presetPolicy?.id ?? "");
  const [form, setForm] = useState({
    claim_type: presetPolicy?.product_line ?? "",
    incident_date: "",
    reported_date: "",
    amount: "",
    cause_of_loss: "",
    description: "",
    injury_involved: false,
    reporter_name: "",
    reporter_phone: "",
    loss_line1: "",
    loss_city: "",
    loss_state: "",
    loss_zip: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setPolicyId(presetPolicy?.id ?? "");
      setForm((prev) => ({ ...prev, claim_type: presetPolicy?.product_line ?? prev.claim_type }));
    }
  }, [open, presetPolicy]);

  useEffect(() => {
    if (customer || !search.trim() || presetPolicy) {
      setResults([]);
      return;
    }
    let active = true;
    const handle = setTimeout(() => {
      clientsApi
        .list(search.trim())
        .then((data) => active && setResults(data.items.slice(0, 8)))
        .catch(() => active && setResults([]));
    }, 250);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [search, customer, presetPolicy]);

  useEffect(() => {
    if (!customer) {
      setPolicies([]);
      return;
    }
    policiesApi
      .list(undefined, customer.id)
      .then((data) => {
        setPolicies(data.items);
        if (data.items.length === 1) setPolicyId(data.items[0].id);
      })
      .catch(() => setPolicies([]));
  }, [customer]);

  function update<K extends keyof typeof form>(field: K, value: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!policyId) {
      setError("Select the policy this claim is filed against.");
      return;
    }
    if (!form.claim_type.trim()) {
      setError("Enter a claim type.");
      return;
    }
    if (!form.incident_date) {
      setError("Enter the incident date.");
      return;
    }
    const amountCents = Math.round(parseFloat(form.amount) * 100);
    if (!form.amount.trim() || Number.isNaN(amountCents) || amountCents <= 0) {
      setError("Enter a claim amount greater than zero.");
      return;
    }
    setSaving(true);
    try {
      const lossLocation = Object.fromEntries(
        ([
          ["line1", form.loss_line1],
          ["city", form.loss_city],
          ["state", form.loss_state],
          ["zip", form.loss_zip],
        ] as const)
          .map(([k, v]) => [k, v.trim()])
          .filter(([, v]) => v !== ""),
      );
      const created = await claimsApi.createFnol({
        policy_id: policyId,
        claim_type: form.claim_type.trim(),
        amount_cents: amountCents,
        incident_date: form.incident_date,
        reported_date: form.reported_date || undefined,
        cause_of_loss: form.cause_of_loss.trim() || undefined,
        description: form.description.trim() || undefined,
        injury_involved: form.injury_involved,
        reporter_name: form.reporter_name.trim() || undefined,
        reporter_phone: form.reporter_phone.trim() || undefined,
        loss_location: Object.keys(lossLocation).length ? lossLocation : undefined,
      });
      onClose();
      if (onCreated) onCreated(created.id);
      else navigate(`/claims/${created.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to file claim.");
      setSaving(false);
    }
  }

  return (
    <Modal open={open} title="File a claim (FNOL)" onClose={onClose} width="38rem">
      <form onSubmit={onSubmit} style={{ display: "grid", gap: "0.85rem" }}>
        {presetPolicy ? (
          <div style={labelWrap}>
            <span className="field-label">Policy</span>
            <strong>{presetPolicy.label}</strong>
          </div>
        ) : (
          <>
            <div style={labelWrap}>
              <span className="field-label">Client</span>
              {customer ? (
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <strong>{customer.name}</strong>
                  <button type="button" className="btn btn-secondary" onClick={() => setCustomer(null)}>
                    Change
                  </button>
                </div>
              ) : (
                <div style={{ position: "relative" }}>
                  <input
                    style={inputStyle}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search client by name, company, or email"
                  />
                  {results.length > 0 ? (
                    <div
                      className="card"
                      style={{ position: "absolute", zIndex: 5, width: "100%", marginTop: 4, padding: "0.25rem" }}
                    >
                      {results.map((client) => (
                        <button
                          key={client.id}
                          type="button"
                          className="btn btn-secondary"
                          style={{ display: "block", width: "100%", textAlign: "left", marginBottom: 2 }}
                          onClick={() => {
                            setCustomer({
                              id: client.id,
                              name: customerName(client.first_name, client.last_name, client.company_name),
                            });
                            setSearch("");
                          }}
                        >
                          {customerName(client.first_name, client.last_name, client.company_name)}
                          {client.email ? ` · ${client.email}` : ""}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {customer ? (
              <label style={labelWrap}>
                <span className="field-label">Policy</span>
                <select style={inputStyle} value={policyId} onChange={(e) => setPolicyId(e.target.value)}>
                  <option value="">Select a policy…</option>
                  {policies.map((policy) => (
                    <option key={policy.id} value={policy.id}>
                      {policy.policy_number} · {policy.product_line ?? "—"}
                    </option>
                  ))}
                </select>
                {policies.length === 0 ? (
                  <span className="field-hint">This client has no policies yet. Add a policy first.</span>
                ) : null}
              </label>
            ) : null}
          </>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
          <label style={labelWrap}>
            <span className="field-label">Claim type</span>
            <input style={inputStyle} value={form.claim_type} onChange={(e) => update("claim_type", e.target.value)} placeholder="Commercial Auto" />
          </label>
          <label style={labelWrap}>
            <span className="field-label">Incident date</span>
            <input style={inputStyle} type="date" value={form.incident_date} onChange={(e) => update("incident_date", e.target.value)} />
          </label>
        </div>

        <label style={labelWrap}>
          <span className="field-label">Estimated amount ($)</span>
          <input style={inputStyle} inputMode="decimal" value={form.amount} onChange={(e) => update("amount", e.target.value)} placeholder="42000.00" />
        </label>

        <label style={labelWrap}>
          <span className="field-label">Description</span>
          <textarea
            style={{ ...inputStyle, minHeight: "4.5rem", resize: "vertical" }}
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            placeholder="What happened?"
          />
        </label>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
          <label style={labelWrap}>
            <span className="field-label">Cause of loss</span>
            <input style={inputStyle} value={form.cause_of_loss} onChange={(e) => update("cause_of_loss", e.target.value)} placeholder="Collision, fire, water…" />
          </label>
          <label style={labelWrap}>
            <span className="field-label">Reported date</span>
            <input style={inputStyle} type="date" value={form.reported_date} onChange={(e) => update("reported_date", e.target.value)} />
          </label>
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <input
            type="checkbox"
            checked={form.injury_involved}
            onChange={(e) => update("injury_involved", e.target.checked)}
          />
          <span>Injury involved</span>
        </label>

        <div style={{ borderTop: "1px solid var(--border)", paddingTop: "0.6rem" }}>
          <span className="field-label" style={{ display: "block", marginBottom: "0.5rem" }}>Loss location</span>
          <div style={{ display: "grid", gap: "0.6rem" }}>
            <input style={inputStyle} placeholder="Street address" value={form.loss_line1} onChange={(e) => update("loss_line1", e.target.value)} />
            <div style={{ display: "grid", gridTemplateColumns: "1.6fr 0.8fr 0.9fr", gap: "0.6rem" }}>
              <input style={inputStyle} placeholder="City" value={form.loss_city} onChange={(e) => update("loss_city", e.target.value)} />
              <select style={inputStyle} value={form.loss_state} onChange={(e) => update("loss_state", e.target.value)}>
                <option value="">State</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <input style={inputStyle} placeholder="ZIP" value={form.loss_zip} onChange={(e) => update("loss_zip", e.target.value)} />
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
          <label style={labelWrap}>
            <span className="field-label">Reported by</span>
            <input style={inputStyle} value={form.reporter_name} onChange={(e) => update("reporter_name", e.target.value)} placeholder="Name of person reporting" />
          </label>
          <label style={labelWrap}>
            <span className="field-label">Reporter phone</span>
            <input style={inputStyle} value={form.reporter_phone} onChange={(e) => update("reporter_phone", e.target.value)} />
          </label>
        </div>

        {error ? <div className="error-banner">{error}</div> : null}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.25rem" }}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Filing…" : "File claim"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
